import type { Group } from '../../types/database.types'

interface GroupCardProps {
  group: Group & { member_count: number }
  onEdit?: () => void
  onDelete?: () => void
  onManageMembers?: () => void
}

const categoryIcons: Record<string, React.ReactNode> = {
  sports_team: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M9 8h6m-6 4h6m-6 4h6M5 8h.01M5 12h.01M5 16h.01M19 8h.01M19 12h.01M19 16h.01" />
    </svg>
  ),
  pt_cohort: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  gym_class: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  custom: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
}

const categoryLabels: Record<string, string> = {
  sports_team: 'Sports Team',
  pt_cohort: 'PT Cohort',
  gym_class: 'Gym Class',
  custom: 'Custom',
}

const categoryColors: Record<string, string> = {
  sports_team: 'bg-blue-100 text-blue-800',
  pt_cohort: 'bg-pink-100 text-pink-800',
  gym_class: 'bg-green-100 text-green-800',
  custom: 'bg-gray-100 text-gray-800',
}

export function GroupCard({
  group,
  onEdit,
  onDelete,
  onManageMembers,
}: GroupCardProps) {
  const category = group.category || 'custom'

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${categoryColors[category]}`}>
            {categoryIcons[category] || categoryIcons.custom}
          </div>
          <div>
            <button
              type="button"
              onClick={onManageMembers}
              className="font-semibold text-gray-900 hover:text-blue-600 text-left"
            >
              {group.name}
            </button>
            {group.description && (
              <p className="mt-0.5 text-sm text-gray-500 line-clamp-1">
                {group.description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <span className={`px-2 py-0.5 text-xs rounded-full ${categoryColors[category]}`}>
          {categoryLabels[category] || 'Custom'}
        </span>
      </div>

      <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-gray-400">
          Created {group.created_at ? new Date(group.created_at).toLocaleDateString() : 'Unknown'}
        </span>
        <div className="flex gap-2">
          {onManageMembers && (
            <button
              onClick={onManageMembers}
              className="text-sm text-green-600 hover:text-green-800"
            >
              Members
            </button>
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="text-sm text-red-500 hover:text-red-700"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
